import React, { useState, useEffect } from 'react';
import { Container, Text } from 'components';
import { hooks, mainOperations } from 'state';
import { FiRefreshCw } from 'react-icons/fi';
import './edge-subscriptions-manager.css';
import { getUserFromSubcookies } from 'utils/tools';
import EdgeSubscriptionForm from './EdgeSubscriptionForm';
import type { EdgeSubscriptionData, EdgeSubscriptionFormData } from './types';
import { mapApiItemToEdgeSubscription, mapFormDataToPutPayload, extractListFromResponse } from './utils';

export interface EdgeSubscriptionDuplicateModalProps {
  edgeGuid: string;
}

const EdgeSubscriptionDuplicateModal: React.FC<EdgeSubscriptionDuplicateModalProps> = ({
  edgeGuid,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [subscription, setSubscription] = useState<EdgeSubscriptionData | null>(null);
  const [existingNames, setExistingNames] = useState<string[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { useAppDispatch } = hooks;
  const { setModalOpen, setSelectedModalContent, getEdgeSubscriptionByGuid, createEdgeSubscription, getEdgeSubscriptions } = mainOperations;
  const dispatch = useAppDispatch();

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!edgeGuid) {
        setIsFetching(false);
        setFetchError('Invalid subscription ID');
        return;
      }
      setIsFetching(true);
      setFetchError(null);
      try {
        const [detailResult, listResult] = await Promise.all([
          dispatch(getEdgeSubscriptionByGuid(edgeGuid)),
          dispatch(getEdgeSubscriptions()),
        ]);
        const detailPayload = detailResult.payload as { edgeSubscription?: Record<string, unknown> } | undefined;
        const data = detailPayload?.edgeSubscription;
        const listPayload = listResult.payload as { edgeSubscriptions?: unknown } | undefined;
        const allListItems = extractListFromResponse(listPayload?.edgeSubscriptions)
          .filter((x): x is Record<string, unknown> => x != null && typeof x === 'object');
        // Buscar en la lista el mismo registro para usar como fallback de campos faltantes
        const listItem = allListItems.find(x =>
          String(x.edgeGuid ?? x.id ?? x.guid ?? '') === edgeGuid
        );
        if (data && typeof data === 'object') {
          // Merge: listItem como base (tiene todos los campos), detail como override (es más reciente)
          const merged: Record<string, unknown> = { ...(listItem ?? {}), ...(data as Record<string, unknown>) };
          setSubscription(mapApiItemToEdgeSubscription(merged));
          const allNames = allListItems
            .map(x => String(x.subscriptionName ?? '').trim())
            .filter(Boolean);
          setExistingNames(allNames);
        } else {
          setFetchError('Subscription not found');
        }
      } catch (err) {
        console.warn('[EdgeSubscriptionDuplicate] Fetch error:', err);
        setFetchError('Failed to load subscription');
      } finally {
        setIsFetching(false);
      }
    };
    fetchSubscription();
  }, [edgeGuid, dispatch, getEdgeSubscriptionByGuid, getEdgeSubscriptions]);

  const handleSubmit = async (formData: EdgeSubscriptionFormData) => {
    setIsLoading(true);
    setSaveError(null);
    try {
      const userGuid = getUserFromSubcookies()?.profile?.sub ?? '';
      const payload = mapFormDataToPutPayload(formData, userGuid);
      await dispatch(createEdgeSubscription(payload)).unwrap();
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
        dispatch(setModalOpen(true));
        dispatch(setSelectedModalContent('edgeSubscriptions'));
      }, 3000);
    } catch (error: any) {
      const code = error?.errorCode ?? error?.code ?? '';
      if (code === 'InvalidVmiqBrand' || String(error?.message ?? '').includes('InvalidVmiqBrand')) {
        setSaveError('The selected brand is not available for Inventory Edge in this VMiQ account. Please choose a different brand.');
      } else {
        setSaveError('An error occurred while saving the subscription. Please try again.');
      }
      console.error('Error duplicating subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <Container className="edge-subscription-modal-body" style={{ padding: '24px', width: '100%' }}>
        <div className="empty-state">
          <FiRefreshCw size={48} className="spin" />
          <Text textStyle="h3" color="text">Loading subscription...</Text>
        </div>
      </Container>
    );
  }

  if (fetchError || !subscription) {
    return (
      <Container style={{ padding: '24px' }}>
        <Text textStyle="h2" style={{ marginBottom: '16px' }}>
          Duplicate Subscription
        </Text>
        <Text>{fetchError ?? 'Subscription not found.'}</Text>
      </Container>
    );
  }

  const initialValues: EdgeSubscriptionFormData = {
    subscriptionName: `${subscription.subscriptionName} (copy)`,
    edgeType: subscription.edgeType,
    recipient: subscription.recipient ?? subscription.recipientEmail,
    state: subscription.state,
    brandName: subscription.brandName,
    periodicity: subscription.periodicity,
    category: subscription.category,
    territory: subscription.territory,
    startDate: subscription.startDate,
    nextDelivery: subscription.nextDelivery,
    sendOnce: subscription.sendOnce,
    externalMessage: subscription.externalMessage,
  };

  return (
    <Container className="edge-subscription-modal-body" style={{ padding: '24px', width: '100%' }}>
      <EdgeSubscriptionForm
        initialValues={initialValues}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        submitLabel="Save duplicate"
        existingNames={existingNames}
      />
      {showSuccessMessage && (
        <div className="success-message" style={{ marginTop: '12px' }}>
          ✅ Subscription duplicated successfully!
        </div>
      )}
      {saveError && (
        <div className="error-message" style={{ marginTop: '12px' }}>
          ❌ {saveError}
        </div>
      )}
    </Container>
  );
};

export default EdgeSubscriptionDuplicateModal;
